import { c as _c } from "react/compiler-runtime";
import type * as PlotModule from "@observablehq/plot";
import { useCallback, useEffect, useRef } from "react";
import { getRemInPixels } from "../../../../main/blocks/Paragraph/Citation";
import { useDebouncedCallback } from "use-debounce";
import styles from "./AreaGraph.module.scss";

/**
 * Plot (and the d3 modules it pulls in) is only needed to draw the chart,
 * which happens client-side after mount. Load it on first draw so it stays
 * out of the JS every page loads. The server HTML is the same empty container
 * either way, and once loaded, redraws are synchronous apart from a microtask.
 */
let plotModule: Promise<typeof PlotModule> | undefined;
const loadPlot = () => plotModule ??= import("@observablehq/plot");
const drawChart = (Plot: typeof PlotModule, data: {
  x: number;
  y: number;
}[], lineInput: number, lineInputWealthPercentage: number, afterDonationWealthPercentage: number, donationPercentage: number, incomePercentileLabelTemplateString: string, afterDonationPercentileLabelTemplateString: string, adjustedPPPConversionFactor: number, periodAdjustment: WealthCalculatorPeriodAdjustment, size: {
  width: number | undefined;
  height: number | undefined;
}) => {
  const incomeXPositon = lineInput / periodAdjustment / adjustedPPPConversionFactor;
  const incomeAfterDonationXPosition = lineInput * (1 - donationPercentage) / periodAdjustment / adjustedPPPConversionFactor;
  const dataMax = Math.max(...data.map(d => d.y));
  // Browser window width smaller than or equal to 1180px
  const isMobile = window.innerWidth <= 1180;
  const wealthPercentileText = convertNumberToBoldText(lineInputWealthPercentage, true);
  const donationPercentageText = (donationPercentage * 100).toLocaleString("no-NB") + "%";
  const afterDonationWealthPercentileText = convertNumberToBoldText(afterDonationWealthPercentage, true);
  const afterDonationLabel = afterDonationPercentileLabelTemplateString.replace("{percentile}", afterDonationWealthPercentileText).replace("{donationpercentage}", donationPercentageText);
  const incomePercentileLabel = incomePercentileLabelTemplateString.replace("{percentile}", wealthPercentileText).replace("{donationpercentage}", donationPercentageText);
  const labelLineWidth = 13;
  const labelLineNumber = Math.round(afterDonationLabel.length / (labelLineWidth * 1.5) + incomePercentileLabel.length / (labelLineWidth * 1.5));
  const requiredMarginTopAsPercentage = size.height ? getBrowserRemSizeInPx(0.8) * 1.3 * labelLineNumber / (size.height - getBrowserRemSizeInPx(0.7) * 1.2 * 2) : 0.4;
  const adjustedMax = dataMax / (1 - requiredMarginTopAsPercentage);
  const numberOfLinesInChart = 6 / requiredMarginTopAsPercentage;
  const adjustedBelowMax = adjustedMax * ((numberOfLinesInChart - 4) / numberOfLinesInChart);
  let incomeMarkers: any[] = [];
  if (lineInput >= 2.7 * periodAdjustment) {
    incomeMarkers = [Plot.ruleX([incomeXPositon], {
      y: adjustedBelowMax,
      ariaHidden: "true"
    }), Plot.text([incomePercentileLabel], {
      lineWidth: labelLineWidth,
      lineHeight: 1.3,
      textAnchor: isMobile ? "end" : "start",
      frameAnchor: "top",
      fontSize: getBrowserRemSizeInPx(0.8),
      fontFamily: "ESKlarheitGrotesk",
      stroke: "var(--secondary)",
      fill: "var(--primary)",
      paintOrder: "stroke",
      strokeWidth: 10,
      x: incomeXPositon,
      y: adjustedBelowMax,
      dx: isMobile ? -10 : 10
    }), Plot.text([afterDonationLabel], {
      lineWidth: labelLineWidth,
      lineHeight: 1.3,
      textAnchor: isMobile ? "end" : "start",
      frameAnchor: "top",
      fontSize: getBrowserRemSizeInPx(0.8),
      fontFamily: "ESKlarheitGrotesk",
      x: incomeAfterDonationXPosition,
      y: adjustedMax,
      dx: isMobile ? -10 : 10
    })];
  }
  const domainLower = 2.6 * periodAdjustment;
  const domainUpper = isMobile ? Math.max(5480 * periodAdjustment, lineInput) : Math.max(8219 * periodAdjustment, lineInput);
  const shouldInsetRight = lineInput > 1700 * periodAdjustment && !isMobile;
  const chart = Plot.plot({
    height: size.height,
    width: size.width,
    marginTop: isMobile ? getRemInPixels() * 3 : 0,
    padding: 20,
    x: {
      type: "log",
      domain: [domainLower, domainUpper],
      insetRight: shouldInsetRight ? 180 : 0,
      transform: (dailyIncome: number) => dailyIncome * periodAdjustment * adjustedPPPConversionFactor,
      label: null
    },
    y: {
      axis: null
    },
    style: {
      background: "var(--secondary)",
      fontSize: "0.7rem",
      fontFamily: "ESKlarheitGrotesk",
      fill: "var(--primary)"
    },
    marks: [Plot.areaY(data, {
      curve: "natural",
      x: "x",
      y: "y",
      ariaHidden: "true"
    }), Plot.ruleX([incomeAfterDonationXPosition], {
      strokeDasharray: "4,4",
      y: adjustedMax,
      ariaHidden: "true"
    }), ...incomeMarkers]
  });
  return chart;
};
export const AreaChart: React.FC<{
  data: {
    x: number;
    y: number;
  }[];
  lineInput: number;
  donationPercentage: number;
  wealthPercentile: number;
  afterDonationWealthPercentile: number;
  label?: string;
  incomePercentileLabelTemplateString: string;
  afterDonationPercentileLabelTemplateString: string;
  adjustedPPPConversionFactor: number;
  periodAdjustment: WealthCalculatorPeriodAdjustment;
}> = t0 => {
  const $ = _c(22);
  const {
    data,
    lineInput,
    donationPercentage,
    wealthPercentile,
    afterDonationWealthPercentile,
    label,
    incomePercentileLabelTemplateString,
    afterDonationPercentileLabelTemplateString,
    adjustedPPPConversionFactor,
    periodAdjustment
  } = t0;
  const outputRef = useRef(null);
  const drawRequest = useRef(0);
  let t1;
  if ($[0] !== adjustedPPPConversionFactor || $[1] !== afterDonationPercentileLabelTemplateString || $[2] !== afterDonationWealthPercentile || $[3] !== data || $[4] !== donationPercentage || $[5] !== incomePercentileLabelTemplateString || $[6] !== lineInput || $[7] !== periodAdjustment || $[8] !== wealthPercentile) {
    t1 = async () => {
      const request = drawRequest.current = drawRequest.current + 1;
      const Plot = await loadPlot();
      if (request !== drawRequest.current) {
        return;
      }
      if (outputRef.current) {
        if (!outputRef.current.parentElement) {
          return;
        }
        outputRef.current.innerHTML = "";
        const parent = outputRef.current.parentElement;
        const width = Math.max(0, Math.floor(parent.getBoundingClientRect().width) - 1);
        const height = Math.max(0, Math.floor(parent.getBoundingClientRect().height) - 1);
        const size = {
          width,
          height
        };
        const chart = drawChart(Plot, data, lineInput, wealthPercentile, afterDonationWealthPercentile, donationPercentage, incomePercentileLabelTemplateString, afterDonationPercentileLabelTemplateString, adjustedPPPConversionFactor, periodAdjustment, size);
        chart.style.overflow = "hidden";
        chart.style.maxWidth = "100%";
        chart.style.width = "100%";
        outputRef.current.appendChild(chart);
      }
    };
    $[0] = adjustedPPPConversionFactor;
    $[1] = afterDonationPercentileLabelTemplateString;
    $[2] = afterDonationWealthPercentile;
    $[3] = data;
    $[4] = donationPercentage;
    $[5] = incomePercentileLabelTemplateString;
    $[6] = lineInput;
    $[7] = periodAdjustment;
    $[8] = wealthPercentile;
    $[9] = t1;
  } else {
    t1 = $[9];
  }
  const drawGraph = t1;
  let t2;
  if ($[10] === Symbol.for("react.memo_cache_sentinel")) {
    t2 = {
      trailing: true
    };
    $[10] = t2;
  } else {
    t2 = $[10];
  }
  const debouncedDrawGraph = useDebouncedCallback(drawGraph, 100, t2);
  let t3;
  let t4;
  if ($[11] !== drawGraph) {
    t3 = () => {
      drawGraph();
    };
    t4 = [drawGraph];
    $[11] = drawGraph;
    $[12] = t3;
    $[13] = t4;
  } else {
    t3 = $[12];
    t4 = $[13];
  }
  useEffect(t3, t4);
  let t5;
  let t6;
  if ($[14] !== debouncedDrawGraph) {
    t5 = () => {
      if (outputRef.current && outputRef.current.parentElement) {
        const parent_0 = outputRef.current.parentElement;
        const observer = new ResizeObserver(() => {
          debouncedDrawGraph();
        });
        observer.observe(parent_0);
        return () => observer.disconnect();
      }
    };
    t6 = [outputRef, debouncedDrawGraph];
    $[14] = debouncedDrawGraph;
    $[15] = t5;
    $[16] = t6;
  } else {
    t5 = $[15];
    t6 = $[16];
  }
  useEffect(t5, t6);
  let t7;
  if ($[17] !== label) {
    t7 = label ? <span className={styles.label}>{label}</span> : null;
    $[17] = label;
    $[18] = t7;
  } else {
    t7 = $[18];
  }
  let t8;
  if ($[19] === Symbol.for("react.memo_cache_sentinel")) {
    t8 = <div ref={outputRef} className={styles.chart} />;
    $[19] = t8;
  } else {
    t8 = $[19];
  }
  let t9;
  if ($[20] !== t7) {
    t9 = <>{t7}{t8}</>;
    $[20] = t7;
    $[21] = t9;
  } else {
    t9 = $[21];
  }
  return t9;
};
const convertNumberToBoldText = (number: number, percentage: boolean = false) => {
  const str = number.toLocaleString("no-NB");
  const split = str.split("");
  let result = "";
  for (let i = 0; i < split.length; i++) {
    const char = split[i];
    // 0 to 9 are located at utf code U+03e2 and upwards, with comma trailing after 9 and % after comma
    switch (char) {
      case "0":
        result += "Ϣ";
        break;
      case "1":
        result += "ϣ";
        break;
      case "2":
        result += "Ϥ";
        break;
      case "3":
        result += "ϥ";
        break;
      case "4":
        result += "Ϧ";
        break;
      case "5":
        result += "ϧ";
        break;
      case "6":
        result += "Ϩ";
        break;
      case "7":
        result += "ϩ";
        break;
      case "8":
        result += "Ϫ";
        break;
      case "9":
        result += "ϫ";
        break;
      case ",":
        result += "Ϭ";
        break;
      default:
        result += char;
    }
  }
  if (percentage) {
    result += "ϭ";
  }
  return result;
};
const getBrowserRemSizeInPx = (rems: number = 1) => {
  return rems * parseFloat(getComputedStyle(document.documentElement).fontSize);
};
export enum WealthCalculatorPeriodAdjustment {
  MONTHLY = 365 / 12,
  YEARLY = 365,
}